/**
 * OperationsPanel — dark NOC/SOC console rendered when the Operations tab
 * is active. Forced dark via [data-ops-panel] regardless of the active theme.
 *
 * Visual reference: 04-hybrid-recommended.html #ops-view block.
 * Composition: live ticker + workload×lens heatmap + monospace live tail
 * on the left; priority alerts rail on the right. The right rail collapses
 * below the main column on smaller viewports.
 *
 * Signal wiring:
 *   - heatmap, tail, lastUpdated — useOperationsData (shared caches)
 *   - priority alerts rail       — useOperationsAlerts (criticals-only,
 *                                   per the curation doctrine in
 *                                   .agents/Tasks/High Priority/
 *                                   operations-priority-alerts-curation.md §3)
 *
 * Both hooks share the active-drift query key with PillarCompliance, so
 * opening Operations after Posture fires no extra fetch for the drift list.
 */

import { useNavigate } from 'react-router-dom'
import type { DashboardStats, M365Workload, UserInsightsSummary } from '@/types'
import type { HeatLevel } from '@/lib/workloadMapping'
import { useOperationsData, type HeatmapCell } from '@/hooks/useOperationsData'
import { useOperationsAlerts } from '@/hooks/useOperationsAlerts'
import type { OperationsAlert } from '@/lib/operations-alerts'

interface Props {
  tenantId: string | undefined
  guardianScore: number | null
  stats: DashboardStats | null | undefined
  insights: UserInsightsSummary | null | undefined
  bpaScore: number | null | undefined
}

export default function OperationsPanel({
  tenantId,
  guardianScore,
  stats,
  insights,
  bpaScore,
}: Props) {
  const navigate = useNavigate()
  const baseline = stats?.complianceScore?.percentage ?? null
  const mfa = insights?.mfaMemberAdoptionPercentage ?? null

  // Heatmap, tail, sync from the shared multi-pillar composer.
  const operations = useOperationsData(tenantId, stats)
  const { heatmap, tail, lastUpdated } = operations

  // Priority alerts rail — criticals-only feed per the curation doctrine.
  const {
    alerts,
    totalCandidates,
    hiddenCountsBySource,
    nonCriticalDriftCount,
    isLoading: alertsLoading,
    error: alertsError,
  } = useOperationsAlerts(tenantId)

  const overflow = totalCandidates > alerts.length
  // Open-alerts ticker reflects the rail's curated candidate pool, not the
  // raw stats count — that way the ticker and rail badge agree.
  const openAlerts = totalCandidates

  const sync = formatSync(lastUpdated)

  return (
    <div
      data-ops-panel
      id="panel-operations"
      role="tabpanel"
      aria-labelledby="tab-operations"
      className="
        relative overflow-hidden rounded-3xl
        bg-dark-900 text-slate-100
        shadow-[0_1px_2px_rgba(33,41,56,0.06),0_24px_60px_-10px_rgba(33,41,56,0.12)]
      "
      style={{
        backgroundImage: `
          radial-gradient(ellipse 80% 50% at 50% -10%, rgba(118,214,157,0.10), transparent 60%),
          linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)
        `,
        backgroundSize: 'auto, 40px 40px, 40px 40px',
      }}
    >
      <div className="grid gap-px bg-white/5 lg:grid-cols-[1fr_360px]">
        {/* Main column */}
        <div className="bg-transparent p-9">
          {/* Ticker */}
          <div
            className="
              mb-7 flex flex-wrap items-center gap-x-7 gap-y-2 rounded-xl
              border border-white/10 bg-[#212938cc] px-5 py-3
              text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60
            "
          >
            <Ticker label="Baseline" value={baseline} />
            <Ticker label="BPA" value={bpaScore ?? null} tone={(bpaScore ?? 0) < 75 ? 'warn' : 'ok'} />
            <Ticker label="MFA" value={mfa} />
            <Ticker label="Open alerts" value={openAlerts} unit="" tone={openAlerts > 0 ? 'crit' : 'ok'} />
            <Ticker label="Sync" value={sync.label} unit="" tone={sync.tone} />
            {guardianScore != null && <Ticker label="Score" value={guardianScore} unit="" />}
          </div>

          {/* Heatmap */}
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-white">
              Workload · Risk Heatmap
            </h3>
            <div className="flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/60">
              <Legend swatch="rgba(118,214,157,0.9)" label="healthy" />
              <Legend swatch="rgba(167,211,240,0.55)" label="watch" />
              <Legend swatch="rgba(235,212,180,0.7)" label="warn" />
              <Legend swatch="rgba(229,115,115,0.85)" label="critical" />
            </div>
          </div>

          {heatmap.workloads.length === 0 ? (
            <p className="mb-8 text-[12px] italic leading-[1.55] text-white/55">
              All five workloads are quiet — no drift, violation, or coverage signal to surface.
            </p>
          ) : (
            <div
              className="mb-8 grid gap-1"
              style={{ gridTemplateColumns: `110px repeat(${heatmap.workloads.length}, 1fr)` }}
            >
              <span aria-hidden />
              {heatmap.workloads.map(w => (
                <span
                  key={w}
                  className="text-center text-[9px] font-bold uppercase tracking-[0.14em] text-white/40 py-1.5"
                >
                  {w}
                </span>
              ))}
              {heatmap.rows.map(row => (
                <HeatRow
                  key={row.label}
                  label={row.label}
                  cells={row.cells}
                  workloads={heatmap.workloads}
                  onCellSelect={hrefForHeatCell(row.label)}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {/* Live tail */}
          <div className="rounded-2xl border border-white/10 bg-[#212938aa] px-6 py-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-white">
                Live Tail
              </h3>
              <span
                className="
                  rounded-full border border-primary-500/30 bg-primary-500/15 px-2.5 py-0.5
                  text-[10px] font-semibold text-primary-500
                "
              >
                ● tailing
              </span>
            </div>
            <div
              className="
                relative max-h-72 overflow-hidden rounded-xl border border-white/[0.06] bg-[#0F131C]
                px-4 py-3.5 font-mono text-[11px] leading-[1.7] text-white/60
              "
            >
              {tail.length === 0 ? (
                <div className="italic text-white/45">No activity in the last 24 hours.</div>
              ) : (
                tail.map((line, i) => (
                  <div key={i}>
                    <span className="mr-2.5 text-white/40">{line.stamp}</span>
                    <span className={LEVEL_CLASS[line.level]}>[{line.level.toUpperCase()}]</span>{' '}
                    {line.msg}
                  </div>
                ))
              )}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0F131C] to-transparent" />
            </div>
          </div>
        </div>

        {/* Right rail — priority alerts */}
        <aside className="bg-[#212938cc] border-l border-white/[0.06] p-7">
          <h4 className="mb-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/60">
            Priority Alerts
            <PriorityAlertsInfo />
            {alerts.length > 0 && (
              <span className="ml-auto rounded-full bg-[#E57373] px-2 py-0.5 text-[10px] font-semibold tracking-normal text-white">
                {overflow ? `${alerts.length} of ${totalCandidates}` : alerts.length}
              </span>
            )}
          </h4>

          {alertsLoading ? (
            <AlertSkeletonList count={4} />
          ) : alertsError ? (
            <p
              className="rounded-lg border border-white/[0.06] bg-[#2C354799] px-3.5 py-3 text-[12px] italic leading-snug text-white/60"
              role="status"
            >
              Couldn't load priority alerts. Refresh to retry.
            </p>
          ) : alerts.length === 0 ? (
            <p className="text-[12px] italic leading-[1.55] text-white/55">
              No criticals open. The rail will populate when something needs attention.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {alerts.map(a => (
                <Alert
                  key={a.id}
                  alert={a}
                  onSelect={href => navigate(href)}
                />
              ))}
            </div>
          )}

          {/* Criticals overflow footer — surfaces hidden criticals when the
              rail truncates (e.g. "8 of 42"). Splits the prompt into
              per-source deep links so operators can land on the right queue,
              narrowed to critical+open. Renders only when overflow is
              non-zero. */}
          {!alertsLoading && !alertsError && overflow && (
            <CriticalOverflowFooter
              hiddenCount={totalCandidates - alerts.length}
              counts={hiddenCountsBySource}
              onNavigate={navigate}
            />
          )}

          {/* Footer link — surfaces non-critical drift backlog without
              admitting it to the rail (per §10 Q1 in the plan). */}
          {!alertsLoading && !alertsError && (
            <NonCriticalDriftFooter
              high={nonCriticalDriftCount.high}
              medium={nonCriticalDriftCount.medium}
              onClick={() => navigate('/configuration-drift?status=Open')}
            />
          )}
        </aside>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const HEAT_COLORS: Record<HeatLevel, string> = {
  l1: 'rgba(118,214,157,0.9)',
  l2: 'rgba(118,214,157,0.55)',
  l3: 'rgba(167,211,240,0.55)',
  l4: 'rgba(235,212,180,0.7)',
  l5: 'rgba(229,115,115,0.85)',
}

const HEAT_LABEL: Record<HeatLevel, string> = {
  l1: 'healthy',
  l2: 'low',
  l3: 'watch',
  l4: 'warn',
  l5: 'critical',
}

function HeatRow({
  label,
  cells,
  workloads,
  onCellSelect,
  navigate,
}: {
  label: string
  cells: Array<HeatmapCell | null>
  workloads: M365Workload[]
  /** Returns the route for a click, or null when the row should stay static. */
  onCellSelect: ((wl: M365Workload) => string | null) | null
  navigate: (path: string) => void
}) {
  return (
    <>
      <span className="self-center pl-1 text-left text-[9px] font-bold uppercase tracking-[0.14em] text-white/70">
        {label}
      </span>
      {cells.map((cell, i) => {
        const wl = workloads[i]
        if (!cell) {
          // No signal for this workload-row combo — render as a transparent
          // gap so the user reads it as "not measured", not "healthy".
          return (
            <span
              key={`${label}-${wl}`}
              className="block h-8 rounded-[4px] border border-dashed border-white/10"
              aria-label={`${label} · ${wl} · not measured`}
              title={`${label} · ${wl} · not measured`}
            />
          )
        }
        const tooltip = `${label} · ${wl} · ${HEAT_LABEL[cell.level]}`
        const href = onCellSelect ? onCellSelect(wl) : null
        if (href) {
          return (
            <button
              key={`${label}-${wl}`}
              type="button"
              onClick={() => navigate(href)}
              className="
                block h-8 rounded-[4px] transition-transform
                hover:scale-110 hover:outline hover:outline-1 hover:outline-white
                focus:outline-none focus-visible:outline focus-visible:outline-1
                focus-visible:outline-white cursor-pointer
              "
              style={{ background: HEAT_COLORS[cell.level] }}
              aria-label={tooltip}
              title={tooltip}
            />
          )
        }
        return (
          <span
            key={`${label}-${wl}`}
            className="block h-8 rounded-[4px] transition-transform hover:scale-110 hover:outline hover:outline-1 hover:outline-white"
            style={{ background: HEAT_COLORS[cell.level] }}
            aria-label={tooltip}
            title={tooltip}
          />
        )
      })}
    </>
  )
}

/**
 * Map a heatmap row label to its drill-in URL builder. Returns null for rows
 * that don't have a clear destination (Coverage today — it's a data-pipeline
 * signal, not a finding, and there's no canonical "see coverage gaps" page).
 */
function hrefForHeatCell(
  rowLabel: string,
): ((wl: M365Workload) => string | null) | null {
  if (rowLabel === 'Best Practice') {
    return wl => `/security?tab=best-practices&workload=${encodeURIComponent(wl)}`
  }
  if (rowLabel === 'Drift') {
    // No workload filter on the drift queue today — the hash takes the user
    // to Section · 03 (the full queue) where they can filter by status /
    // severity / date.
    return () => '/configuration-drift#full-queue'
  }
  return null
}

function Ticker({
  label,
  value,
  unit = '%',
  tone = 'ok',
}: {
  label: string
  value: number | string | null
  unit?: string
  tone?: 'ok' | 'warn' | 'crit'
}) {
  const dotClass =
    tone === 'crit'
      ? 'bg-[#E57373] shadow-[0_0_10px_#E57373] animate-pulse'
      : tone === 'warn'
        ? 'bg-secondarySand-300 shadow-[0_0_8px_rgb(var(--color-secondary-sand-300))]'
        : 'bg-primary-500 shadow-[0_0_8px_rgb(var(--color-primary-500))]'
  const display = value == null ? '—' : typeof value === 'number' ? Math.round(value) : value
  return (
    <span className="inline-flex items-center gap-2.5">
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
      <span>{label}</span>
      <span className="font-mono font-bold text-white tabular-nums">
        {display}
        {value != null && typeof value === 'number' && unit}
      </span>
    </span>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="block h-2.5 w-2.5 rounded-sm" style={{ background: swatch }} />
      <span>{label}</span>
    </span>
  )
}

const LEVEL_CLASS = {
  ok:   'text-primary-500',
  info: 'text-secondaryBlue-300',
  warn: 'text-secondarySand-300',
  crit: 'text-[#E57373]',
} as const

/**
 * Alert row.
 *
 * The left column renders either the event timestamp (drift / Shall rows
 * with a real `detectedAt`) or the literal state label `Open` (Identity
 * rows have no event time — they're current-state findings, and faking a
 * timestamp would mislead operators reading the rail as a feed).
 *
 * Tone is currently always 'crit' per Phase 1 of the curation doctrine;
 * the type stays as a union so the visual treatment can relax later
 * without a refactor.
 */
function Alert({
  alert,
  onSelect,
}: {
  alert: OperationsAlert
  onSelect: (href: string) => void
}) {
  const tone = alert.severity
  const borderColor =
    tone === 'crit'
      ? '#E57373'
      : tone === 'high'
        ? 'rgb(var(--color-secondary-sand-300))'
        : 'rgb(var(--color-secondary-blue-300))'
  const sourceColor =
    tone === 'crit'
      ? 'text-[#E57373]'
      : tone === 'high'
        ? 'text-secondarySand-300'
        : 'text-secondaryBlue-300'

  // Identity rows have no detectedAt: render `Open` as a state label so
  // the visual asymmetry matches the data asymmetry honestly.
  const leftColumn = alert.detectedAt
    ? formatAlertStamp(alert.detectedAt)
    : 'Open'

  return (
    <button
      type="button"
      onClick={() => onSelect(alert.href)}
      className="
        grid grid-cols-[auto_1fr] items-start gap-3
        rounded-lg border border-white/[0.06] bg-[#2C354799] px-3.5 py-3 text-left
        transition-colors hover:bg-[#3A445899]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60
      "
      style={{ borderLeft: `3px solid ${borderColor}` }}
    >
      <span className="min-w-[34px] pt-0.5 font-mono text-[10px] font-bold text-white/40">
        {leftColumn}
      </span>
      <span className="text-[12px] leading-snug text-white/80">
        <strong className="font-semibold text-white">{alert.text}</strong>
        {alert.detail && (
          <span className="block text-white/65">{alert.detail}</span>
        )}
        <span
          className={`mt-1 block text-[10px] font-semibold uppercase tracking-[0.12em] ${sourceColor}`}
        >
          {alert.sourceLabel}
        </span>
      </span>
    </button>
  )
}

/**
 * Info affordance next to the Priority Alerts header. Hover/focus reveals a
 * short explainer so operators don't read the (deliberately curated) count
 * as a backlog total.
 */
function PriorityAlertsInfo() {
  const tip =
    "A watch list of critical detections from the last 14 days, not a ranked fix list. For what to tackle this week by impact, see the Posture queue."
  return (
    <span
      tabIndex={0}
      role="img"
      aria-label={tip}
      title={tip}
      className="
        inline-flex items-center justify-center
        text-white/40 cursor-help
        hover:text-white/80
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 rounded-full
        transition-colors
      "
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    </span>
  )
}

/**
 * Skeleton placeholder list shown while the alerts hook is loading. Mirrors
 * the row chrome (left stamp column + two-line text block + source label)
 * so layout doesn't shift on first paint.
 */
function AlertSkeletonList({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="
            grid grid-cols-[auto_1fr] items-start gap-3
            rounded-lg border border-white/[0.06] bg-[#2C354799] px-3.5 py-3
          "
          style={{ borderLeft: '3px solid rgba(255,255,255,0.08)' }}
        >
          <span className="block h-3 w-8 rounded bg-white/10 animate-pulse" />
          <span className="block">
            <span className="block h-3 w-3/4 rounded bg-white/10 animate-pulse" />
            <span className="mt-2 block h-2.5 w-1/3 rounded bg-white/[0.07] animate-pulse" />
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Criticals overflow footer — renders when the rail truncates (e.g. the
 * curated admit list ran "8 of 42") and offers per-source deep links so
 * operators can land on the right queue, narrowed to critical+open.
 *
 * Voice: subject-led, named subjects ("drift", "SHALL", "identity"), plain
 * hyphen, one short sentence (≤ 14 words). Mirrors NonCriticalDriftFooter's
 * styling and accessibility patterns exactly — same focus ring, same hover,
 * same text size, same button-as-anchor pattern.
 *
 * Each clause is a separate clickable link. Clauses for sources whose count
 * is 0 are skipped. If somehow all three counts are 0 but hiddenCount > 0
 * (a defensive case the math shouldn't allow), render nothing.
 */
function CriticalOverflowFooter({
  hiddenCount,
  counts,
  onNavigate,
}: {
  hiddenCount: number
  counts: { drift: number; shall: number; identity: number }
  onNavigate: (path: string) => void
}) {
  const clauses: Array<{ key: string; label: string; href: string }> = []
  if (counts.drift > 0) {
    clauses.push({
      key: 'drift',
      label: `${counts.drift} drift`,
      href: '/configuration-drift?status=Open&severity=Critical',
    })
  }
  if (counts.shall > 0) {
    clauses.push({
      key: 'shall',
      label: `${counts.shall} SHALL`,
      href: '/security?tab=best-practices&criticality=SHALL&status=Open',
    })
  }
  if (counts.identity > 0) {
    clauses.push({
      key: 'identity',
      label: `${counts.identity} identity`,
      href: '/user-insights?tab=all&scope=globalAdmins',
    })
  }

  if (clauses.length === 0) return null

  return (
    <p className="mt-3 text-[11px] leading-snug text-white/55">
      <span>{hiddenCount} more open. View </span>
      {clauses.map((c, i) => (
        <span key={c.key}>
          <button
            type="button"
            onClick={() => onNavigate(c.href)}
            className="
              text-left underline-offset-2 hover:underline hover:text-white/80
              transition-colors
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 rounded
            "
          >
            {c.label}
          </button>
          {i < clauses.length - 1 ? <span>, </span> : <span>.</span>}
        </span>
      ))}
    </p>
  )
}

/**
 * Footer link below the alerts list — surfaces non-critical drifts also
 * open without admitting them to the criticals-only rail. Renders nothing
 * when there are no high or medium drifts open. Uses a plain hyphen rather
 * than an em-dash to keep with the project voice rules.
 */
function NonCriticalDriftFooter({
  high,
  medium,
  onClick,
}: {
  high: number
  medium: number
  onClick: () => void
}) {
  if (high <= 0 && medium <= 0) return null

  const parts: string[] = []
  if (high > 0) parts.push(`${high} High`)
  if (medium > 0) parts.push(`${medium} Medium`)
  const label = parts.join(' and ')
  // Use plural "drifts" when either count contributes more than 1; if both
  // are exactly 1 it still reads naturally as "1 High and 1 Medium drifts".
  const noun = high === 1 && medium === 0 ? 'drift' : medium === 1 && high === 0 ? 'drift' : 'drifts'

  return (
    <button
      type="button"
      onClick={onClick}
      className="
        mt-3 block w-full text-left
        text-[11px] leading-snug text-white/55
        hover:text-white/80 transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 rounded
      "
    >
      {label} {noun} also open, view in queue.
    </button>
  )
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

const HOUR_MS = 60 * 60 * 1000
const DAY_MS  = 24 * HOUR_MS

function formatSync(iso: string | null): { label: string; tone: 'ok' | 'warn' | 'crit' } {
  if (!iso) return { label: '—', tone: 'warn' }
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return { label: '—', tone: 'warn' }
  const ageMs = Math.max(0, Date.now() - t)

  let label: string
  if (ageMs < 60_000)         label = 'just now'
  else if (ageMs < HOUR_MS)   label = `${Math.floor(ageMs / 60_000)}m`
  else if (ageMs < DAY_MS)    label = `${Math.floor(ageMs / HOUR_MS)}h`
  else                        label = `${Math.floor(ageMs / DAY_MS)}d`

  const tone: 'ok' | 'warn' | 'crit' =
    ageMs < HOUR_MS ? 'ok' : ageMs < DAY_MS ? 'warn' : 'crit'

  return { label, tone }
}

// Alerts use a wall-clock stamp like the original mockup ("14:32") so the
// rail reads as a feed. Falls back to en-dash if the source timestamp is
// missing — better than rendering NaN:NaN.
function formatAlertStamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}
